import type {
  AnnualReportPage,
  AnnualReportSection,
  Maybe,
} from '@/.generated/sanityTypes/graphql'
import type { Locale } from '@/i18n'
import { useLocale, useTranslations } from 'next-intl'
import React from 'react'
import { useTransition, useTrail, animated } from '@react-spring/web'
import { MenuIcon } from './blocks/Icons'
import { cx } from '@monorepo/helper/cx'

type MenuProps = {
  sections?: Maybe<AnnualReportSection>[] | null
  pages?: AnnualReportPage[]
  open: boolean
  onClose: () => void
  year: string
}

const Menu = ({ sections, pages, open, onClose, year }: MenuProps) => {
  const locale = useLocale() as Locale
  const t = useTranslations('menu')
  const sectionMenuItems = (sections ?? []).filter((s) => s?.visibleInMenu)
  const menuItems = [...sectionMenuItems, ...(pages ?? [])]
  const newMenu = year === '2023' || year === '2024'

  // Create a "home" report page for 2023
  if (newMenu) {
    menuItems.unshift({
      __typename: 'AnnualReportPage',
      pageSlug: {
        _key: 'home',
        current: '',
      },
      menuTitle: {
        [locale]: year === '2023' ? t('homeEnergy') : t('home'),
      },
    })
  }

  const openMenutransitions = useTransition(open, {
    from: { width: '0vw', height: '0vh' },
    enter: { width: '100vw', height: '100vh' },
    leave: { width: '0vw', height: '0vh' },
  })

  // This is the critical damping formula
  // friction^2 = 4 * mass * tension
  const mass = 10
  const friction = 600
  const tension = (friction * friction) / (4 * mass)

  const menuItemsTrail = useTrail(menuItems.length, {
    config: { mass, tension, friction },
    opacity: open ? 1 : 0,
    x: open ? 0 : -100,
    from: { opacity: 0, x: -100 },
  })

  return (
    <>
      {openMenutransitions((style, item) => {
        return (
          <>
            {item && (
              <animated.div
                style={style}
                className={cx(
                  'fixed left-0 top-0 z-50 overflow-hidden',
                  newMenu ? 'bg-gray-light' : 'bg-yellow',
                )}
                data-cy="menu"
                onClick={() => onClose()}
              >
                <div className="h-screen w-screen overflow-auto">
                  <button
                    onClick={() => onClose()}
                    title={t('close')}
                    className="fixed left-0 top-0 ml-2 mt-2 inline-block h-12 w-12 cursor-pointer focus:outline-none"
                    data-cy="close-menu-button"
                  >
                    <MenuIcon color="rgb(218, 41, 28)" />
                  </button>
                  <div className="flex min-h-screen flex-col justify-evenly">
                    <div className="pb-16 pt-16 text-center text-3xl sm:text-4xl md:text-5xl">
                      <ul>
                        {menuItemsTrail.map((style, index) => {
                          const menuItem = menuItems[index]
                          if (!menuItem) return null

                          let href = ''
                          // For 2023 only the Annual Report Pages should be shown in the menu
                          if (newMenu) {
                            if (menuItem.__typename === 'AnnualReportPage') {
                              href = `/${locale}/${year}/${
                                (menuItem as AnnualReportPage).pageSlug?.current
                              }`
                            } else {
                              return
                            }
                          } else {
                            href = `#${
                              (menuItem as AnnualReportSection).menuSlug
                                ?.current
                            }`
                          }

                          return (
                            <animated.li key={index} style={style}>
                              <a
                                className={cx(
                                  'hover:text-red block py-1 leading-tight transition-colors duration-200',
                                  // TODO: figure out which section is active
                                  // false ? 'text-red' : 'text-offBlack'
                                  'text-offBlack',
                                )}
                                href={href}
                              >
                                {menuItem.menuTitle![locale] || ''}
                              </a>
                            </animated.li>
                          )
                        })}
                      </ul>
                    </div>
                  </div>
                </div>
              </animated.div>
            )}
          </>
        )
      })}
    </>
  )
}

export default Menu
